/**
 * Filename convention for production files.
 *
 * `{order}_{product}_{size}_r{rowIndex}[-name][-number][_UNGRADED].svg`
 *
 * `rowIndex` (position in product_roster_detail) keeps entries unique even
 * when two roster rows share the same size + name + number.
 */
export interface ProductionFileNameParts {
  orderNo?: string | number | null;
  productName?: string | null;
  size?: string | null;
  rowIndex: number;
  playerName?: string | null;
  playerNumber?: string | number | null;
  ungraded?: boolean;
}

/** Keep letters/digits/dashes; everything else collapses to a single underscore. */
export function sanitizeFileSegment(value: string | number | null | undefined): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return raw
    .replace(/[^A-Za-z0-9-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_');
}

export function buildProductionFileName(parts: ProductionFileNameParts): string {
  const order = sanitizeFileSegment(parts.orderNo) || 'order';
  const product = sanitizeFileSegment(parts.productName) || 'product';
  const size = sanitizeFileSegment(parts.size) || 'nosize';

  let row = `r${parts.rowIndex + 1}`;
  const player = sanitizeFileSegment(parts.playerName);
  const number = sanitizeFileSegment(parts.playerNumber);
  if (player) row += `-${player}`;
  if (number) row += `-${number}`;

  const ungraded = parts.ungraded ? '_UNGRADED' : '';
  return `${order}_${product}_${size}_${row}${ungraded}.svg`;
}
